import escape from '../utils/escape.js';
import BaseComponent from './BaseComponent.js';
import registerTabTrap from '../utils/registerTabTrap.js';

/**
 * Extend the base component to provide common functionality for custom dialogs.
 */
class BaseDialog extends BaseComponent {
    /** @type {HTMLDialogElement} */
    #dialog;

    /**
     * Cleanup function for the active tab trap.
     * @type {(() => void)|undefined}
     */
    #tabTrapCleanup;

    /**
     * Clean up when the component is removed from the document.
     */
    disconnectedCallback() {
        // cleanup should have been called when the dialog closed, but better safe
        this.#tabTrapCleanup?.();
        this.#tabTrapCleanup = undefined;

        super.disconnectedCallback();
    }

    /**
     * Create a dialog element from an HTML string.
     * @param {string} content Text to display in the body of the dialog.
     * @param {string} ok Label for the ok button.
     * @param {string|undefined} cancel Label for the cancel button.
     * @param {(returnValue: string) => void|undefined} onClose Function to run after dialog closes.
     */
    showDialog(content, ok, cancel = undefined, onClose = undefined) {
        if (this.#dialog) {
            return;
        }

        const div = document.createElement('div');
        div.innerHTML = this.#makeDialogHtml(content, ok, cancel);
        this.#dialog = /** @type {HTMLDialogElement} */ (div.firstChild);
        document.body.appendChild(this.#dialog);

        this.#tabTrapCleanup = registerTabTrap(this.#dialog);

        // manually close the dialog when clicking a button instead of using `form method="dialog"` so this is testable
        const buttons = this.#dialog.querySelector('[data-dialog-footer]').querySelectorAll('button');
        for (const x of buttons) {
            x.addEventListener('click', () => {
                this.#dialog.close(x.value);
            });
        }

        this.#dialog.addEventListener('close', () => {
            const { returnValue } = this.#dialog;

            this.#tabTrapCleanup?.();
            this.#tabTrapCleanup = undefined;
            this.#dialog.remove();
            this.#dialog = undefined;

            if (onClose) {
                onClose(returnValue);
            }
        });
        this.#dialog.showModal();
    }

    /**
     * Create the HTML for the dialog element.
     * @param {string} content HTML to display in the body of the dialog.
     * @param {string} ok Text label for the ok button.
     * @param {string|undefined} cancel Text label for the cancel button
     * @returns {string} HTML for the dialog.
     */
    #makeDialogHtml(content, ok, cancel) {
        return `<dialog>
            <p class="p-2">${content}</p>
            <div class="ml-2" data-dialog-footer>${this.#makeDialogButtons(ok, cancel)}</div>
        </dialog>`;
    }

    /**
     * Create the HTML for the dialog button elements.
     * @param {string} ok Label for the ok button.
     * @param {string|undefined} cancel Label for the cancel button
     * @returns {string} HTML for the buttons.
     */
    #makeDialogButtons(ok, cancel) {
        if (cancel) {
            return `<button type="button" class="button primary" value="cancel" autofocus>${escape(cancel)}</button>
                <button type="button" class="button dark" value="ok">${escape(ok)}</button>`;
        }
        return `<button type="button" class="button success" value="ok" autofocus>${escape(ok)}</button>`;
    }
}

export default BaseDialog;
